import React from 'react';
import mekakApiDefault from '../../services/mekakApi';
import { TAG_COLORS } from './models';

export { mekakApiDefault as mekakApi };

// ─── Formatting helpers ─────────────────────────────────────────────────────

/** Format ISO date string → zh-TW with date + time */
export const formatDate = (dateStr: string): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

/** Format ISO date string → zh-TW date only (no time) */
export const formatDateShort = (dateStr: string): string => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

/** Format cost in USD with 6 decimal places */
export const formatCost = (cost?: number | null): string => {
  if (cost === undefined || cost === null) return '-';
  if (cost === 0) return '$0.000000';
  return `$${cost.toFixed(6)}`;
};

/** Format cost in USD (alias for column renderers) */
export const formatCostUSD = formatCost;

/** Format token count with locale separators */
export const formatTokens = (tokens?: number | null): string => {
  if (tokens === undefined || tokens === null) return '-';
  return tokens.toLocaleString();
};

/** Truncate text to max length */
export const truncate = (text: string, maxLength: number): string => {
  if (!text) return '-';
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

// ─── UI helpers ─────────────────────────────────────────────────────────────

/** Deterministic tag color from tag name */
export function tagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

/** Highlight matching keywords in text */
export const highlightText = (text: string, keywords: string[]): React.ReactNode => {
  const active = keywords.filter((k) => k.trim());
  if (active.length === 0) return text;
  try {
    const escaped = active.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(`(${escaped.join('|')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => {
      if (active.some((k) => part.toLowerCase() === k.toLowerCase())) {
        return React.createElement('mark', { key: i, style: { background: '#ffe58f', padding: 0 } }, part);
      }
      return part;
    });
  } catch {
    return text;
  }
};
